import type { CommonResponse } from '@/utils/request';
import { Request } from '@/utils/request';

import type { ECallStatus } from './call';
import type { EFriendType } from './friend';
import type { ISnapConfig, ITalkConfig } from './play';

export interface IMemoryId {
  id: string;
  type: 'NOTE' | 'CHAT';
}

export interface IDialogueSequence {
  content: string;
  sequence: number;
  speakerId: string;
  speakerName: string;
  speakerRole: string;
}

export interface ISlideItem {
  duration?: number;
  title?: string;
  headline?: string;
  imageUrl?: string;
  dialogueSequences?: IDialogueSequence[];

  // 前端
  timePoint?: number;
}

export interface IRecallRes {
  memoryIds?: IMemoryId[];
}

export interface INarration {
  content?: string;
}

export interface ILyrics {
  content?: string;
}

export interface IStoryBoard {
  slides?: ISlideItem[];
  estimatedTime?: string;
  title?: string;
}

export interface IDialogue {
  headline?: string;
  dialogueSequences?: IDialogueSequence[];
}

export interface IParamsInit {
  participant: IParticipant[];
  instruction: string;
}

export interface INarrationItem {
  headline: string;
  content: string;
}

export interface IAudio {
  audioUrl: string;
}

export interface ISnapComposing {
  prompt: string;
}

export interface IMusic {
  audioUrl: string;
  mixedMusicUrl?: string;
  duration?: string;
}

export interface ICompleteOutput {
  title?: string;
  cover?: string;
  shareCode?: string;
  imageUrl?: string;
}

export interface IIdentifyOutputBaseItem {
  id: string;
  title: string;
  cover: string;
  shareCode?: string;
  remixMode?: RemixMode;
  outputMode?: EOutputMode;
  playCount?: string;
}

export interface IIdentifyOutputListItem extends IIdentifyOutputBaseItem {
  status: EStatus;
  instruction?: string;
  avatars?: string[];
  participants?: IParticipant[];
}

export interface IAudioAlign {
  content: string;
  endTime: number;
  id: string;
  startTime: number;
  userId?: string;
}

export interface IIdentityOutput extends IIdentifyOutputListItem {
  totalDuration?: number;
  audioUrl?: string;
  imageUrls?: string[];
  slides?: ISlideItem[];
  recallRes?: IRecallRes[] | undefined;
  memoryIds?: IMemoryId[];
  narrations?: INarrationItem[];
  narrationString?: string;
  audioAlign?: IAudioAlign[];
  msgDialogue?: IDialogueSequence[];
  estimatedTime?: string;
}

export interface ISnapTemplate {
  id: string;
  name: string;
  image: string;
}

export interface ISnapParticipant {
  userId: string;
  userName: string;
  avatar: string;
}

export enum ESnapType {
  DUET = 'DUET',
  SOLO = 'SOLO'
}

export interface ISnapCreation {
  id: string;
  shareCode?: string;
  template?: ISnapTemplate;
  image: string;
  status: EStatus;
  createTime: string;
  exposeCount?: number;
  participants?: ISnapParticipant[];
  instruction?: string;
  snapType?: ESnapType;
}

export interface ICreationItem {
  type: EOutputMode;
  id: string;
  talk?: IIdentifyOutputListItem;
  snap?: ISnapCreation;
  call?: ICallCreation;
}

export interface ICallCreation {
  id?: number;
  templateId?: string;
  title?: string;
  status?: ECallStatus;
  callCount?: number;
  author?: {
    id?: string;
    username?: string;
    avatar?: string;
    route?: string;
    cover?: string;
  };
}

export interface ICreationData {
  total: number;
  list: [];
}

export enum OUTPUT_TEMPLATE {
  OFFER = 'offer',
  RIZZ = 'rizz'
}

// export interface OUTPUT_TEMPLATE_DATA_OFFER {
//   name: string;
//   salary: string;
//   image_url: string;
// }

// export interface OUTPUT_TEMPLATE_DATA_RIZZ {
//   description: string;
//   score: string;
// }

type OUTPUT_TEMPLATE_DATA = {
  // OFFER
  name?: string;
  salary?: string;
  image_url?: string;
  // RIZZ
  description?: string;
  score?: string;
};

export interface IIdentityOutputShare extends IIdentityOutput {
  userInfo: {
    name: string;
    avatar: string;
    id: string;
    route: string;
  };
  supportFork?: boolean;
  sessionId?: string;
  createTime?: string;
  ogImage?: string;
  // template
  templateName?: OUTPUT_TEMPLATE;
  templateData?: OUTPUT_TEMPLATE_DATA;
}

export interface IIdentitySession {
  sessionId: string;
  latestMessage: string;
  latestMessageTime: string;
  newMessageCount: number;
  targetUser: {
    id: string;
    username: string;
    avatar: string;
  };
}

export interface IIdentityRelatedSessionRes {
  identityOutput: IIdentifyOutputBaseItem;
  sessionList: IIdentitySession[];
}

export interface IAudioChunkSSE {
  slideIndex: number;
  audioChunk: string;
}

export enum EStatus {
  DOING = 'doing',
  DONE = 'done',
  ERROR = 'error',
  UNDONE = 'undone',
  WARNING = 'warn',
  SUCCESS = 'success'
}

export interface ISSEResponse<T> {
  status: EStatus;
  data?: T;
}

export interface ISSESnapGenerating {
  status: EStatus;
  estimatedEndTime: number;
}

export interface ISSESnapComplete {
  status: EStatus;
  recordId?: string;
  shareCode?: string;
  imageUrl?: string;
}

export interface IIdentityOutputSave {
  id: string;
  instruction: string;
}

export enum EOutputMode {
  VIDEO = 'video',
  MSG = 'msg',
  MUSIC = 'music',
  SNAP = 'snap',
  CALL = 'call'
}

export interface IIdentityOutputSaveParams {
  id?: string;
  title?: string;
  instruction?: string;
  memoryIds?: IMemoryId[];
  slides?: ISlideItem[];
  remixMode?: RemixMode | null;
  sendToSession?: boolean;
  outputMode?: EOutputMode;
  participants?: {
    userId: string;
    roleInScenario: EParticipantRole.PARTICIPANT;
  }[];
  forkFromId?: string;
  saveOnly?: boolean;
  templateName?: OUTPUT_TEMPLATE;
  attachments?: string[];
  circleRoute?: string;
}

export const getIdentityOutputById = (params: { id: string }) => {
  return Request<CommonResponse<IIdentityOutput>>({
    url: '/rest/os/identity/output/get',
    method: 'get',
    params
  });
};

export const getIdentityRelatedSessionRes = (params: { id: string }) => {
  return Request<CommonResponse<IIdentityRelatedSessionRes>>({
    url: '/rest/os/identity/output/related/session',
    method: 'get',
    params
  });
};

export const getIdentityOutputList = (params?: {
  pageNo: number;
  pageSize: number;
  includeMsgTalk?: boolean;
}) => {
  return Request<CommonResponse<{ list: IIdentifyOutputListItem[]; total: number }>>({
    url: '/rest/os/identity/output/list',
    method: 'get',
    params: {
      pageNo: params?.pageNo || 1,
      pageSize: params?.pageSize || 999,
      includeMsgTalk: params?.includeMsgTalk || true
    }
  });
};

export const getIdentityOutputListV2 = (params?: {
  pageNo: number;
  pageSize: number;
  outputModes?: EOutputMode[];
}) => {
  return Request<CommonResponse<{ list: IIdentifyOutputListItem[]; total: number }>>({
    url: '/rest/os/identity/output/list_v2',
    method: 'get',
    params: {
      pageNo: params?.pageNo || 1,
      pageSize: params?.pageSize || 999,
      outputModes: params?.outputModes || []
    }
  });
};

export const getCreationList = (params?: {
  pageNo: number;
  pageSize: number;
  types?: EOutputMode[];
}) => {
  return Request<CommonResponse<ICreationData>>({
    url: '/rest/os/playground/creation/list',
    method: 'get',
    params: {
      pageNo: params?.pageNo || 1,
      pageSize: params?.pageSize || 999,
      types: params?.types?.join(',')
    }
  });
};

export const generateShareCode = (params: { id: string }) => {
  return Request<CommonResponse<string>>({
    url: '/rest/os/identity/output/share',
    method: 'get',
    params
  });
};

export const getIdentityOutputByShareCode = (params: { shareCode: string }) => {
  return Request<CommonResponse<IIdentityOutputShare>>({
    url: '/rest/os/identity/output/get/by/share',
    method: 'get',
    params
  });
};

export const identityOutputSaveAndGenerate = (data: IIdentityOutputSaveParams) => {
  return Request<CommonResponse<IIdentityOutputSave>>({
    url: '/rest/os/identity/output/save/and/generate',
    method: 'post',
    data
  });
};

export const deleteIdentityOutput = (data: { id: string }) => {
  return Request<CommonResponse<void>>({
    url: '/rest/os/identity/output/deleteById',
    method: 'post',
    data
  });
};

export const updateRecall = (params: { instruction: string }) => {
  return Request<CommonResponse<{ memoryIds: IMemoryId[] }>>({
    url: '/rest/os/identity/output/memoryRecall/by/instruction',
    method: 'get',
    params
  });
};

export enum RemixMode {
  TALK_TO_ME = 'talk_to_me',
  TALK_WITH_ME = 'talk_with_me'
}

export interface ICelebrities {
  userName: string;
  userId: string;
  avatar: string;
  creatorName?: string;
  cover?: string;
  voiceSetting?: boolean;
}

export enum EParticipantRole {
  PARTICIPANT = 'participant',
  INITIATOR = 'initiator'
}

export interface IParticipant {
  userId: string;
  role: EParticipantRole;
  route: string;
  avatar: string;
  userName: string;
  cover: string;
  memoryIds?: IMemoryId[];
}

export const getCelebrities = (params?: { language?: string }) => {
  return Request<CommonResponse<ICelebrities[]>>({
    url: '/rest/os/multi/identity/output/celebrities',
    method: 'get',
    params
  });
};

export const getUseCaseList = (params?: { language?: string }) => {
  return Request<CommonResponse<IIdentifyOutputListItem[]>>({
    url: '/rest/os/multi/identity/output/propagatables',
    method: 'get',
    params
  });
};

export const getVideo = (params: { identityId: string; resolution: 'landscape' | 'portrait' }) => {
  return Request<CommonResponse<string>>({
    url: '/rest/os/identity/output/video/download',
    method: 'get',
    params
  });
};

export const exposeOutputOpen = (data: { id: string }) => {
  return Request<CommonResponse<any>>({
    url: '/rest/os/identity/output/play/record',
    method: 'post',
    data
  });
};

export interface IInviteFriendItem {
  friendId: string;
  name: string;
  avatar: string;
  route: string;
  remarks?: string;
}

export const getInviteFriendList = (params?: {
  pageNo: number;
  pageSize: number;
  type: EFriendType;
}) => {
  return Request<CommonResponse<{ list: IInviteFriendItem[]; total: number }>>({
    url: '/rest/os/friend/simple/list',
    method: 'get',
    params
  });
};

export interface ISparkInvite {
  id: string;
  instruction: string;
  participant?: {
    id: string;
    username: string;
    avatar: string;
    route: string;
  };
  demo: IIdentifyOutputListItem;
  ogImage: string;
}

export const getSparkInvite = (params: {
  talkId?: string;
  participantId: string;
  excludeScriptId?: string;
}) => {
  return Request<CommonResponse<ISparkInvite>>({
    url: '/rest/os/identity/output/get/script',
    method: 'get',
    params
  });
};
export interface IAIPlay {
  bubbleId: string;
  /**
   * 气泡类型
   * spark: AI 脑洞
   * photosTogether: AI合拍: 双人自由 prompt
   * selfie: AI 合拍: 单人自由 prompt
   * chorus: AI 合唱
   * atmosphericContent: 氛围内容，单纯文案展示，只能关闭
   */
  bubbleType: 'spark' | 'photosTogether' | 'selfie' | 'chorus' | 'atmosphericContent';
  description: string;
  prompt: string;
  isUsed: boolean; // 后端字段，表示是否已经被使用，我这里借用，兼表示是否要展示
}

export const getBubble = (data: {
  friendId: string;
  sessionId: string;
  forceRefresh?: boolean;
}) => {
  return Request<CommonResponse<IAIPlay>>({
    url: '/rest/os/friend/chat/recommendation',
    method: 'post',
    data
  });
};

export const spendBubble = (data: {
  bubbleId: string;
  useType?: IAIPlay['bubbleType'];
  activityId?: string | number;
}) => {
  return Request<CommonResponse<IAIPlay>>({
    url: '/rest/os/friend/chat/recommendation/bubble/use',
    method: 'post',
    data
  });
};

export const getInvitationCreate = (data: {
  type: 'SNAP' | 'TALK';
  snapConfig?: ISnapConfig;
  talkConfig?: ITalkConfig;
}) => {
  return Request<CommonResponse<{ inviteCode: string }>>({
    url: '/rest/os/playground/invitation/create',
    method: 'post',
    data
  });
};

export const getExportVideo = (data: { id: string }) => {
  return Request<CommonResponse<{ status: string; videoUrl: string }>>({
    url: `/rest/os/simple-video-export/${data.id}`,
    method: 'post'
  });
};
